import Splide from '@splidejs/splide';
import '@splidejs/splide/css/core';
import '../../scss/acf-blocks/acfb-carousel.scss';
import { uniqueCarouselName, carouselA11yOptions } from '../components/carousel-a11y';

/**
 * Wraps each direct child of .splide__list in a .splide__slide div.
 * This is needed because InnerBlocks render their content directly,
 * without the wrapping element that Splide requires.
 *
 * @param {HTMLElement} carousel - The carousel root element.
 */
function wrapSlides(carousel) {
	const list = carousel.querySelector('.splide__list');
	if (!list) return;

	[...list.children].forEach((child) => {
		// Skip if already wrapped.
		if (child.classList.contains('splide__slide')) return;

		const slide = document.createElement('div');
		slide.className = 'splide__slide';
		list.insertBefore(slide, child);
		slide.appendChild(child);
	});
}

/**
 * Initialize all Splide carousels on the page.
 * Supports multiple instances via the for-loop pattern.
 *
 * @see https://splidejs.com/guides/getting-started/#multiple-carousels
 */
/**
 * Creates a fill-style progress bar inside the --fill pagination container
 * and updates it on every slide change.
 *
 * @param {Splide} splide - The mounted Splide instance.
 */
function initFillPagination(splide) {
	const container = splide.root.querySelector('.splide__pagination--fill');
	if (!container) return;

	// Create the progress bar element.
	const bar = document.createElement('li');
	bar.className = 'splide__pagination--fill__bar';
	container.appendChild(bar);

	const update = () => {
		const total = splide.Components.Slides.getLength(true);
		const index = splide.index;
		const perPage = splide.options.perPage;
		// La barra cresce da (perPage / total) a 100% man mano che scrolli.
		const barWidth = ((perPage + index) / total) * 100;
		bar.style.width = `${barWidth}%`;
	};

	// Set initial state and listen for changes.
	splide.on('mounted move', update);
}

/**
 * Crea l'istanza Splide con i default JS (i valori ACF in data-splide li
 * sovrascrivono) e il nome accessibile del carosello nelle frecce.
 * Il nome univoco è calcolato una volta sola e salvato sull'elemento, così la
 * re-inizializzazione dei tab accordion non lo rinumera.
 *
 * @see https://splidejs.com/guides/options/#by-data-attribute
 * @param {HTMLElement} el - La root .splide.
 * @returns {Splide}
 */
function createSplide(el) {
	if (!el.dataset.carouselA11yName) {
		el.dataset.carouselA11yName = uniqueCarouselName(el.dataset.carouselName);
	}

	return new Splide(el, {
		type: 'slide',
		height: 'auto',
		perPage: 4,
		perMove: 1,
		gap: '1em',
		arrows: true,
		pagination: false,
		arrowPath: 'M2.052 20H37.949M20 2.051 37.949 20 20 37.949',
		...carouselA11yOptions(el.dataset.carouselA11yName),
	});
}

function initCarousels() {
	const carousels = document.querySelectorAll('.carousel .splide');

	carousels.forEach((el) => {
		// Wrap InnerBlocks children into splide__slide elements.
		wrapSlides(el);

		// Apply max height to first child of each slide if set.
		const maxHeight = el.dataset.carouselMaxh;
		if (maxHeight) {
			const slides = el.querySelectorAll('.splide__slide');
			slides.forEach((slide) => {
				const firstChild = slide.firstElementChild;
				if (firstChild) {
					// Calcola il padding verticale e sottrai dall'altezza totale
					const computed = window.getComputedStyle(firstChild);
					const paddingTop = parseFloat(computed.paddingTop) || 0;
					const paddingBottom = parseFloat(computed.paddingBottom) || 0;
					const totalPadding = paddingTop + paddingBottom;

					// Applica l'altezza sottraendo il padding
					const finalHeight = maxHeight - totalPadding;
					firstChild.style.height = finalHeight + 'px';
				}
			});
		}

		const splide = createSplide(el);

		// ── Custom pagination (read from data attribute) ──────────
		const paginationType = el.dataset.carouselPagination || 'none';

		if (paginationType === 'fill') {
			initFillPagination(splide);
		}
		// Future: else if ( paginationType === 'segment' ) { … }

		splide.mount();
	});
}

document.addEventListener('DOMContentLoaded', initCarousels);

/**
 * Ascolta l'evento di attivazione del tab accordion
 * e aggiorna il layout dei carousel dentro il tab attivato
 */
window.addEventListener('accordionTabActivated', function(e) {
	const tabElement = e.detail?.tabElement;
	if (!tabElement) return;

	// Reinizializza Splide instances dentro il tab attivato con delay maggiore
	const carousels = tabElement.querySelectorAll('.carousel .splide');
	carousels.forEach((el) => {
		setTimeout(() => {
			if (el.splide) {
				// Destroy l'istanza precedente
				el.splide.destroy();
				el.splide = null;
			}

			// Ricrea l'istanza Splide con le stesse opzioni
			const splide = createSplide(el);

			const paginationType = el.dataset.carouselPagination || 'none';
			if (paginationType === 'fill') {
				initFillPagination(splide);
			}

			splide.mount();
		}, 100);
	});
});
